import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Seo, { productJsonLd } from "@/shop/Seo";
import { apiGet, fmt } from "@/shop/api";
import { useCart } from "@/shop/CartContext";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const CONDITION_LABELS = {
  collector: "Collector",
  excellent: "Eccellente",
  used: "Usato",
  workhorse: "Workhorse",
};

export default function ArchiveProduct() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    setError(false);
    setImgIdx(0);
    apiGet(`/api/shop/products/${slug}`)
      .then((prod) => {
        setP(prod);
        window.posthog?.capture("product_view", {
          sku: prod.sku, name: prod.name, type: "archive",
        });
        window.posthog?.capture("archive_view", { sku: prod.sku });
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-serif italic text-2xl">Questo oggetto non è più nell'archivio.</p>
        <Link to="/shop/archive" className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember hover:underline">
          Torna all'archivio
        </Link>
      </div>
    );
  if (!p) return <div className="bg-ink min-h-screen" />;

  const images = p.images?.length ? p.images : [{ url: "/photos/carpa-notturna.jpg", alt: p.name }];
  const sold = p.sold;

  const rows = [
    ["Produttore", p.manufacturer],
    ["Modello", p.model],
    ["Epoca", p.era],
    ["Condizione", CONDITION_LABELS[p.condition] || p.condition],
    ["Componenti originali", p.components],
    ["Modifiche", p.modifications],
    ["Difetti noti", p.defects],
    ["Manutenzione", p.service_history],
    ["Provenienza", p.provenance],
  ].filter(([, v]) => v);

  const add = () => {
    addItem(p, 1);
    toast.success(`${p.name} — aggiunto al carrello`);
  };

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="archive-product">
      <Seo
        title={p.seo_title || `${p.manufacturer || "Archive"} ${p.model || p.name} — Stillwater Archive`}
        description={p.seo_description || p.short_description || p.description?.slice(0, 155)}
        canonical={`https://stillwatermag.eu/shop/archive/${p.slug}`}
        jsonLd={productJsonLd(p, `/shop/archive/${p.slug}`)}
        ogType="product"
      />

      <div className="px-5 md:px-10 pt-24 md:pt-28 pb-4">
        <Link
          to="/shop/archive"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint hover:text-ember transition-colors"
        >
          ← Archive
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 px-5 md:px-10 pb-16">
        <div>
          <motion.div
            key={imgIdx}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="aspect-[4/5] md:aspect-[4/3] overflow-hidden border border-ink-border"
          >
            <img
              src={images[imgIdx].url}
              alt={images[imgIdx].alt || `${p.name} — foto ${imgIdx + 1}`}
              className={`h-full w-full object-cover ${sold ? "saturate-[0.6] opacity-80" : ""}`}
            />
          </motion.div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button
                  key={im.url + i}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-20 md:w-24 aspect-square overflow-hidden border transition-colors ${i === imgIdx ? "border-ember" : "border-ink-border hover:border-faint"}`}
                >
                  <img src={im.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-4">
          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ember">
            Archive — pezzo {sold ? "archiviato" : "disponibile"}
          </p>
          <h1 className="mt-3 font-serif font-light text-3xl md:text-5xl tracking-tight leading-tight">
            {p.name}
          </h1>
          <p className="mt-5 font-mono text-xl md:text-2xl text-bone">
            {fmt(p.price)}
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-faint">IVA incl.</span>
          </p>

          {sold ? (
            <div className="mt-8 border border-ember text-ember inline-flex font-mono text-xs tracking-[0.4em] px-6 py-3.5" data-testid="sold-banner">
              SOLD — QUESTO PEZZO È TROVATO CASA
            </div>
          ) : (
            <button
              data-testid="add-to-cart"
              onClick={add}
              className="mt-8 inline-flex items-center gap-3 bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-8 py-4 rounded-full hover:bg-ember-bright transition-colors duration-300"
            >
              <ShoppingBag size={14} strokeWidth={1.5} />
              Aggiungi al carrello — pezzo unico
            </button>
          )}

          {p.short_description && (
            <p className="mt-8 text-sm text-mist leading-relaxed">{p.short_description}</p>
          )}

          <div className="mt-10 space-y-10">
            {p.description && (
              <section data-testid="the-object">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint border-t border-ink-border pt-6">
                  L'oggetto
                </h2>
                <p className="mt-4 text-sm md:text-base text-bone/90 leading-relaxed whitespace-pre-line">
                  {p.description}
                </p>
              </section>
            )}
            {(p.condition_note || p.condition) && (
              <section data-testid="condition-section">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint border-t border-ink-border pt-6">
                  Condizione
                </h2>
                <span className="inline-block mt-4 font-mono text-[10px] uppercase tracking-[0.25em] border border-bone/30 text-bone px-3 py-1.5 rounded-full">
                  {CONDITION_LABELS[p.condition] || p.condition}
                </span>
                {p.condition_note && (
                  <p className="mt-4 text-sm text-mist leading-relaxed whitespace-pre-line">
                    {p.condition_note}
                  </p>
                )}
              </section>
            )}
            {rows.length > 0 && (
              <section data-testid="archive-details">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint border-t border-ink-border pt-6">
                  Dettagli
                </h2>
                <dl className="mt-4">
                  {rows.map(([k, v]) => (
                    <div key={k} className="py-3 border-b border-ink-border">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint">{k}</dt>
                      <dd className="mt-1 font-serif text-sm text-bone/90 leading-relaxed whitespace-pre-line">{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
